import { NotificationManager } from "react-notifications";
import { Actions as INTEGRATION_ACTIONS } from "../../../../store/actions/integrationActions";

import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { createRequest } from "@/common/helpers/RequestHelpers";
import { camelCase } from "lodash";
import apiURL from "../../../../store/actions/apiURL";

const integrationRequest = createRequest({
	urlPath: "/integrations",
	// local: true,
});

const Types = {
	PAYMENT_INTEGRATION_UPDATE_CONNECTION: "PAYMENT_INTEGRATION_UPDATE_CONNECTION",
	PAYMENT_INTEGRATION_UPDATE_EMPLOYEE: "PAYMENT_INTEGRATION_UPDATE_EMPLOYEE",
	PAYMENT_INTEGRATION_UNSYNCED_EMPLOYEE: "PAYMENT_INTEGRATION_UNSYNCED_EMPLOYEE",
	PAYMENT_INTEGRATION_PAYMENT_STAGE: "PAYMENT_INTEGRATION_PAYMENT_STAGE",
	PAYMENT_INTEGRATION_SYNCING_STATUS: "PAYMENT_INTEGRATION_SYNCING_STATUS",
	PAYMENT_INTEGRATION_PAY_PERIOD: "PAYMENT_INTEGRATION_PAY_PERIOD",
	PAYMENT_INTEGRATION_COMPANIES: "PAYMENT_INTEGRATION_COMPANIES",
	PAYMENT_INTEGRATION_ACCOUNTS: "PAYMENT_INTEGRATION_ACCOUNTS",
	PAYMENT_INTEGRATION_SAVE: "PAYMENT_INTEGRATION_SAVE",
	PAYMENT_INTEGRATION_SAVING: "PAYMENT_INTEGRATION_SAVING",
	PAYMENT_INTEGRATION_UPDATE_FIELD: "PAYMENT_INTEGRATION_UPDATE_FIELD",
};

const saveComplete = (integration, data) => ({
	type: Types.PAYMENT_INTEGRATION_SAVE,
	integration,
	data,
});

const saving = (integration) => ({
	type: Types.PAYMENT_INTEGRATION_SAVING,
	integration,
});

const updateField = (integration, field, value) => ({
	type: Types.PAYMENT_INTEGRATION_UPDATE_FIELD,
	integration: camelCase(integration),
	field,
	value,
});
const updateEmployeeState = (data) => ({
	type: Types.PAYMENT_INTEGRATION_UPDATE_EMPLOYEE,
	paymentIntegrationId: data.paymentIntegrationId,
	pending: data.pending,
});
const updateSyncingState = (data) => ({
	type: Types.PAYMENT_INTEGRATION_SYNCING_STATUS,
	unsyncedEmployees: data.unsyncedEmployees,
	integration: data.integration,
	error: data.error,
	syncingStatus: data.syncingStatus,
});
const setPaymentStage = (integration, stage) => ({
	type: Types.PAYMENT_INTEGRATION_PAYMENT_STAGE,
	integration,
	stage,
});
const setPaymentIntegrationCompanies = (integration, companies) => ({
	type: Types.PAYMENT_INTEGRATION_COMPANIES,
	integration,
	companies,
});
const setPaymentIntegrationAccounts = (integration, accounts) => ({
	type: Types.PAYMENT_INTEGRATION_ACCOUNTS,
	integration,
	accounts,
});
const updatePayPeriod = (integration, response) => ({
	type: Types.PAYMENT_INTEGRATION_PAY_PERIOD,
	integration,
	response,
});

const connectPaymentIntegration = (integration, credentials) => {
	return (dispatch, getState) => {
		const integrationCamelCase = camelCase(integration);

		integrationRequest({
			method: "GET",
			path: `/${integrationCamelCase}/new`,
		})
			.then(({ data }) => {
				if (!data.url) {
					NotificationManager.error(`Error Connecting ${integration}`, `Connect ${integration}`);
					return;
				}
				if (!data.url.startsWith("https://api.buddy.hr") && !data.url.startsWith("http://localhost")) {
					window.open(data.url, "_self");
				} else {
					const url = data.url.replace("https://api.buddy.hr/", "").replace("http://localhost:3001/", "");
					const [version, ...rest] = url.split("/");
					const reconstructedURL = rest.join("/").replace("integrations/", "");

					integrationRequest({
						method: "GET",
						path: `/${reconstructedURL}`,
						query: credentials,
					}).then((data) => {
						dispatch(INTEGRATION_ACTIONS.integrationGetList());
					});
				}
			})
			.catch((error) => {
				console.error(error);
				const body = error.response.data;
				NotificationManager.error(
					`Error Connecting ${integration}${body.error ? " - " + body.error : ""}`,
					`Connect ${integration}`,
				);
			});
	};
};

const verifyConnection = (integration) => {
	return (dispatch, getState) => {
		const integrationCamelCase = camelCase(integration);

		integrationRequest({
			method: "GET",
			path: `/${integrationCamelCase}/verify-connection`,
		})
			.then(({ data }) => {
				switch (data.status) {
					case "Pending":
						NotificationManager.info(
							`Connection With ${integration} Is Still Pending`,
							`Connect ${integration}`,
						);
						break;
					case "Connected":
						NotificationManager.success(`Successfully Connected ${integration}`, `Connect ${integration}`);
						dispatch(INTEGRATION_ACTIONS.integrationGetList());
						break;
					case "Error":
						NotificationManager.error(`Error Connecting ${integration}`, `Connect ${integration}`);
						break;
				}
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Verifying Connection With ${integration}`, `Connect ${integration}`);
			});
	};
};

const disconnectPaymentIntegration = (integration) => {
	return (dispatch, getState) => {
		const integrationCamelCase = camelCase(integration);

		integrationRequest({
			method: "DELETE",
			path: `/${integrationCamelCase}/`,
		})
			.then(() => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
				NotificationManager.success(`Successfully Disconnected ${integration}`, `Disconnect ${integration}`);
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(`Error Disconnecting ${integration}`, `Disconnect ${integration}`);
			});
	};
};

const updateEmployeeRequest = (integration, agreement) => {
	return new Promise(async (resolve, reject) => {
		const integrationCamelCase = camelCase(integration);

		integrationRequest({
			method: "POST",
			path: `/${integrationCamelCase}/update-employee`,
			body: { agreement },
		})
			.then((response) => {
				if (response.status != 200) {
					reject();
				} else {
					resolve(response);
				}
			})
			.catch((error) => {
				reject(error);
			});
	});
};

const updatePaymentIntegrationEmployee = (integration, agreement) => {
	return (dispatch, getState) => {
		updateEmployeeRequest(integration, agreement)
			.then((response) => response.json())
			.then((data) => {
				dispatch(updateEmployeeState(data));
			})
			.catch((error) => {
				console.error(error);
				dispatch(updateEmployeeState({ pending: true }));
				NotificationManager.error(
					`Error Updating Employee On ${integration}`,
					`${integration} Update Employee`,
				);
			});
	};
};

const getEmployeesToSync = (integration, payPeriodId) => {
	return (dispatch, getState) => {
		const integrationCamelCase = camelCase(integration);
		fetch(apiURL + `/integrations/${integrationCamelCase}/resync/${payPeriodId}/employees`, {
			credentials: "include",
			method: "GET",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
			},
		})
			.then((response) => response.json())
			.then((data) => {
				data.integration = integrationCamelCase;
				dispatch(updateSyncingState(integration, data));
			})
			.catch((error) => {
				console.error(error);
				dispatch(updateSyncingState(integration, { error: true }));
				NotificationManager.error("Error Getting Employees", `${integration} Sync Employees`);
			});
	};
};

const getPayPeriodStatus = (integration, payPeriodId) => {
	return async (dispatch, getState) => {
		const integrationCamelCase = camelCase(integration);

		integrationRequest({
			method: "GET",
			path: `/${integrationCamelCase}/status/${payPeriodId}`,
		})
			.then(({ data: response }) => {
				if (response.status == "error") {
					NotificationManager.error(
						`Error Getting Sending Payroll to ${integration}`,
						`Send Payroll to ${integration}`,
					);
				}

				if (response.status == "success") {
					NotificationManager.success(
						`Successfully Sent Payroll to ${integration}`,
						`Send Payroll to ${integration}`,
					);
				}

				dispatch(updatePayPeriod(integrationCamelCase, response));
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(
					`Error Getting Updated ${integration} Status`,
					`${integration} Payroll Status`,
				);
			});
	};
};

const sendPayrollToPaymentIntegration = (integration, payPeriodId, transferDate) => {
	return async (dispatch, getState) => {
		let state = getState();

		if (state?.integration?.payment?.[integration]?.syncError) {
			NotificationManager.error("Failed to Sync All Employees", `${integration} Sync Employees`);
			dispatch(setPaymentStage(integration, "Error"));
		} else {
			if (state?.integration?.payment?.[integration]?.unsyncedEmployees?.length > 0) {
				NotificationManager.success("Successfully Synced All Employees", `${integration} Sync Employees`);
			}

			dispatch(setPaymentStage(integration, "Sending"));

			const integrationCamelCase = camelCase(integration);

			// await new Promise((resolve, reject) => {
			//     setTimeout(() => { resolve() }, 5000);
			// })

			integrationRequest({
				method: "POST",
				path: `/${integrationCamelCase}/pay`,
				body: { payPeriodId, transferDate },
			})
				.then(({ data: response }) => {
					dispatch(setPaymentStage(integration, "Done"));
					NotificationManager.success(
						`Successfully Sent Payments to ${integration}`,
						`${integration} Sync Payroll`,
					);
					dispatch(COMPANY_ACTIONS.setBankTransferModal());
					dispatch(INTEGRATION_ACTIONS.integrationGetList());
				})
				.catch((error) => {
					console.error(error);
					dispatch(setPaymentStage(integration, "Error"));
					NotificationManager.error("Failed to Send Payments", `${integration} Sync Payroll`);
					setTimeout(() => {
						dispatch(INTEGRATION_ACTIONS.integrationGetList());
					}, 2000);
				})
		}
	};
};

const openPaymentModal = (integration, periodId) => {
	return (dispatch, getState) => {
		const state = getState();

		const period = state.payPeriods?.periods?.find((period) => period.id === periodId);

		dispatch(COMPANY_ACTIONS.setBankTransferModal(camelCase(integration)));
		dispatch(COMPANY_ACTIONS.setBankTransferPeriod(period));
	};
};

const syncEmployees = (integration, payPeriodId, transferDate) => {
	return async (dispatch, getState) => {
		let state = getState();

		const integrationCamelCase = camelCase(integration);
		const unsyncedEmployees = state?.integration?.payment?.[integration]?.unsyncedEmployees;

		dispatch(setPaymentStage(integration, "Syncing"));

		if (unsyncedEmployees) {
			await Promise.all(
				unsyncedEmployees.map(async (employee, index) => {
					// await new Promise((resolve, reject) => { //Fake Request For Demo
					//     setTimeout(() => { resolve() }, (index + 1)*1000);
					// })
					await updateEmployeeRequest(integrationCamelCase, employee)
						.then(() => {
							dispatch(
								updateSyncingState(integrationCamelCase, {
									syncingStatus: ((index + 1) / unsyncedEmployees?.length) * 100,
								}),
							);
							console.log("syncemployees- success");
						})
						.catch(() => {
							dispatch(updateSyncingState(integrationCamelCase, { error: true }));
							console.log("syncemployees- error");
						});
				}),
			);
		}

		dispatch(sendPayrollToPaymentIntegration(integration, payPeriodId, transferDate));
	};
};

const getCompanies = (integration) => {
	return async (dispatch, getState) => {
		let state = getState();

		const integrationCamelCase = camelCase(integration);
		// dispatch(setPaymentStage(integration,'Syncing'))

		integrationRequest({
			method: "GET",
			path: `/${integrationCamelCase}/companies`,
		})
			.then(({ data }) => {
				data.integration = integrationCamelCase;
				dispatch(setPaymentIntegrationCompanies(integrationCamelCase, data));
			})
			.catch((error) => {
				console.error(error);
				// dispatch(updateSyncingState(integration,{ error: true }))
				NotificationManager.error("Error Getting Employees", `${integration} Sync Employees`);
			});
	};
};

const getAccounts = (integration) => {
	return async (dispatch, getState) => {
		let state = getState();

		const integrationCamelCase = camelCase(integration);
		// dispatch(setPaymentStage(integration,'Syncing'))

		let companyId = "";
		if (state.integration.payment?.[integrationCamelCase]?.settings?.companyId) {
			companyId = `/${state.integration.payment?.[integrationCamelCase]?.settings?.companyId}`;
		}

		integrationRequest({
			method: "GET",
			path: `/${integrationCamelCase}/accounts${companyId}`,
		})
			.then(({ data }) => {
				data.integration = integrationCamelCase;
				dispatch(setPaymentIntegrationAccounts(integrationCamelCase, data));
			})
			.catch((error) => {
				console.error(error);
				// dispatch(updateSyncingState(integration,{ error: true }))
				NotificationManager.error("Error Getting Accounts", `${integration} Get Accounts`);
			});
	};
};
const linkOrganisation = (integration, companyId) => {
	return async (dispatch, getState) => {
		let state = getState();

		const integrationCamelCase = camelCase(integration);

		integrationRequest({
			method: "POST",
			path: `/${integrationCamelCase}/link/organisation`,
			body: { companyId },
		})
			.then(({ data }) => {
				data.integration = integrationCamelCase;
				dispatch(saveComplete(integrationCamelCase, data));
				NotificationManager.success("Successfully Linked Organisation", `${integration} Link Organisation`);
			})
			.catch((error) => {
				console.error(error);
				// dispatch(updateSyncingState(integration,{ error: true }))
				NotificationManager.error("Error Linking Organisation", `${integration} Link Organisation`);
			});
	};
};

const save = (integration) => {
	return async (dispatch, getState) => {
		let state = getState();

		const integrationCamelCase = camelCase(integration);
		dispatch(saving(integration));

		const data = {
			companyId: state.integration.payment[integrationCamelCase]?.details?.companyId,
			accountId: state.integration.payment[integrationCamelCase]?.details?.accountId,
		};

		integrationRequest({
			method: "POST",
			path: `/${integrationCamelCase}`,
			body: data,
		})
			.then(({ data }) => {
				data.integration = integrationCamelCase;
				dispatch(saveComplete(integration, data));
			})
			.catch((error) => {
				console.error(error);
				dispatch(saveComplete(integration, {}));
				NotificationManager.error("Error Saving", `Unable to save your ${integration} settings`);
			});
	};
};

const Actions = {
	connectPaymentIntegration,
	disconnectPaymentIntegration,
	updatePaymentIntegrationEmployee,
	verifyConnection,
	setPaymentStage,
	getEmployeesToSync,
	syncEmployees,
	getPayPeriodStatus,
	getCompanies,
	linkOrganisation,
	getAccounts,
	save,
	updateField,
	openPaymentModal,
};

export { Actions, Types };

